// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { NextgenPractice } from '@/services/practices'
import { normalizeName, PRACTICE_REGEX } from '@/utils/helpers'

export const MISSING_PRACTICE_IDENTIFIERS_MESSAGE =
  'Unable to add practice. Required identifiers are missing (enterpriseId and/or enterprisePracticeId). Please work with the API team to resync this practice to the gateway and try again.'

export const UNSOURCED_PRACTICE_MESSAGE =
  'No matches found. Practices must be configured in LASI before they can be added here.'

export const MISSING_GCN_MESSAGE =
  'This customer has no GCN, so no LASI practices can be retrieved. Practices can only be added to customers sourced from LASI.'

export const INVALID_PRACTICE_NAME_MESSAGE =
  'Use lowercase letters/digits, may include - or _, must start/end with a letter or digit.'

export const DUPLICATE_PRACTICE_MESSAGE =
  'A practice with this name already exists for this customer.'

export const formatPracticeIdentifier = (value?: string) => {
  const trimmed = String(value ?? '').trim()

  if (!trimmed || trimmed.toLowerCase() === 'none') {
    return 'N/A'
  }

  return trimmed
}

export const isMissingPracticeIdentifier = (value?: string) =>
  formatPracticeIdentifier(value) === 'N/A'

export const hasRequiredPracticeIdentifiers = (practice: NextgenPractice) =>
  !isMissingPracticeIdentifier(practice.enterpriseId) &&
  !isMissingPracticeIdentifier(practice.enterprisePracticeId)

export const getNextgenPracticeOptionValue = (practice: NextgenPractice) =>
  [
    practice.practiceName,
    formatPracticeIdentifier(practice.enterpriseId),
    formatPracticeIdentifier(practice.enterprisePracticeId),
    formatPracticeIdentifier(practice.siteId),
    practice.practiceId,
  ].join('::')

/** The tenant name a practice is provisioned under, derived from LASI. */
export const getPracticeTenantName = (practice: NextgenPractice) =>
  normalizeName(practice.practiceName) || practice.practiceName

/**
 * Practices may only be provisioned from a practice returned by LASI, so the
 * selected option must resolve back to one. Returns the rejection reason, or
 * null when the practice is safe to create.
 */
export const getPracticeRejectionReason = (
  practice: NextgenPractice | null | undefined,
  existingPractices: Set<string>
): string | null => {
  if (!practice) return UNSOURCED_PRACTICE_MESSAGE

  const tenantName = getPracticeTenantName(practice)

  if (!PRACTICE_REGEX.test(tenantName)) return INVALID_PRACTICE_NAME_MESSAGE
  if (existingPractices.has(tenantName)) return DUPLICATE_PRACTICE_MESSAGE
  if (!hasRequiredPracticeIdentifiers(practice)) {
    return MISSING_PRACTICE_IDENTIFIERS_MESSAGE
  }

  return null
}
